// Backend API client for Reqqy execution engine
import { config } from './config';

const BACKEND_URL = config.backendUrl;

export class BackendError extends Error {
  constructor(
    message: string,
    public statusCode?: number,
    public path?: string,
    public responseBody?: string,
  ) {
    super(message);
    this.name = 'BackendError';
  }
}

export async function backendGet<T = unknown>(path: string): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${BACKEND_URL}${path}`);
  } catch (err) {
    throw new BackendError(
      `Network error calling backend GET ${path}: ${err instanceof Error ? err.message : String(err)}`,
      undefined,
      path,
    );
  }
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new BackendError(
      `Backend GET ${path} failed: ${res.status} ${text}`,
      res.status,
      path,
      text,
    );
  }
  return res.json();
}

export async function backendPost<T = unknown>(path: string, body?: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${BACKEND_URL}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: body ? JSON.stringify(body) : undefined,
    });
  } catch (err) {
    throw new BackendError(
      `Network error calling backend POST ${path}: ${err instanceof Error ? err.message : String(err)}`,
      undefined,
      path,
    );
  }
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new BackendError(
      `Backend POST ${path} failed: ${res.status} ${text}`,
      res.status,
      path,
      text,
    );
  }
  return res.json();
}

// --- Typed interfaces ---

export interface HealthResponse {
  status: string;
}

export interface SystemInfo {
  engines?: unknown[];
  repository_backend?: string;
  mock_run_ids?: string[];
  [key: string]: unknown;
}

export interface DebugRun {
  id: string;
  module_type?: string;
  status?: string;
  current_step?: string;
  progress_pct?: number;
  created_at?: string;
  started_at?: string;
  completed_at?: string;
  [key: string]: unknown;
}

export interface RunEvent {
  id?: string;
  event_type: string;
  message: string;
  data_payload?: Record<string, unknown>;
  created_at?: string;
}

export interface RunArtifact {
  id?: string;
  artifact_type: string;
  label?: string;
  content_json?: unknown;
  created_at?: string;
  [key: string]: unknown;
}

export interface RunDetails {
  id: string;
  status?: string;
  module_type?: string;
  current_step?: string;
  progress_pct?: number;
  created_at?: string;
  started_at?: string;
  completed_at?: string;
  input_payload?: Record<string, unknown>;
  context_payload?: Record<string, unknown>;
  config_payload?: Record<string, unknown>;
  result_payload?: Record<string, unknown>;
  metrics_payload?: Record<string, unknown>;
  error_payload?: Record<string, unknown>;
  artifacts?: RunArtifact[];
  [key: string]: unknown;
}

export async function checkBackendHealth(): Promise<{ ok: boolean; data?: HealthResponse; error?: string }> {
  try {
    const data = await backendGet<HealthResponse>('/api/health');
    return { ok: true, data };
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : String(err) };
  }
}

// --- Named helper functions ---

/** GET /api/health */
export const getBackendHealth = () => backendGet<HealthResponse>('/api/health');

/** GET /api/debug/system — dev-only system info */
export const getBackendSystem = () => backendGet<SystemInfo>('/api/debug/system');

/** GET /api/debug/runs — dev-only list of available mock runs */
export const getRunsDebug = () => backendGet<DebugRun[]>('/api/debug/runs');

/** POST /api/execute/{runId} — trigger execution */
export const executeRun = (runId: string) => backendPost<RunDetails>(`/api/execute/${runId}`);

/** GET /api/runs/{runId} — proper run details */
export const getRun = (runId: string) => backendGet<RunDetails>(`/api/runs/${runId}`);

/** GET /api/runs/{runId}/events — proper run events */
export const getRunEvents = (runId: string) => backendGet<RunEvent[]>(`/api/runs/${runId}/events`);

/** GET /api/runs/{runId}/artifacts — proper run artifacts */
export const getRunArtifacts = (runId: string) => backendGet<RunArtifact[]>(`/api/runs/${runId}/artifacts`);

// Legacy backendApi object (updated to use proper routes)
export const backendApi = {
  health: getBackendHealth,
  debugSystem: getBackendSystem,
  debugRuns: getRunsDebug,
  executeRun,
  getRun,
  getRunEvents,
  getRunArtifacts,
  /** @deprecated use getRun instead */
  getRunDetails: (runId: string) => backendGet<RunDetails>(`/api/execute/debug/runs/${runId}`),
};

export { BACKEND_URL };
